import { pageFaqAdditions } from "@/data/pageFaqs";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fitCallUrl } from "@/lib/booking";
import { scanUrl } from "@/lib/diagnostic";
import { m } from "framer-motion";
import {
  Flag, Target, TrendingUp, Eye, RefreshCw, ShieldCheck,
  Users, Briefcase, Activity, AlertCircle, BarChart, GitMerge, Maximize,
  Search, Minimize2, Settings, Zap, Lightbulb, Layers, Linkedin, ArrowRight,
} from "lucide-react";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, faqPageSchema, webPageSchema } from "@/lib/seo";
import teamImg from "@/assets/team-collaboration.webp";
import { PhotoBand } from "@/components/site/PhotoBand";
import leadershipImg from "@/assets/photos/leadership-table.webp";
import { getTestimonial, relationshipLabel } from "@/data/testimonials";
import { NextStep } from "@/components/site/NextStep";

const faqs = pageFaqAdditions["/about"];

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "About Ajility: The Integrator for Founder-Led Businesses" },
      {
        name: "description",
        content:
          "Meet Adam Ayoub and learn how Ajility installs operational clarity, accountability and execution discipline. Our purpose, core values, and the Ajility Way.",
      },
      { property: "og:title", content: "About Ajility: The Integrator for Founder-Led Businesses" },
      {
        property: "og:description",
        content:
          "Meet Adam Ayoub and learn how Ajility installs operational clarity, accountability and execution discipline. Our purpose, core values, and the Ajility Way.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "About Ajility: The Integrator for Founder-Led Businesses" },
      { name: "twitter:description", content: "Meet Adam Ayoub and learn how Ajility installs operational clarity, accountability and execution discipline. Our purpose, core values, and the Ajility Way." },
      ...ogImage("about", "About Ajility and Fractional COO Adam Ayoub"),
      ogUrl("/about"),
    ],
    links: canonicalLinks("/about"),
    scripts: [
      jsonLd(faqPageSchema("/about", faqs)),
      jsonLd(breadcrumbSchema([{ name: "About", path: "/about" }])),
      jsonLd(webPageSchema({ image: "about",
        path: "/about",
        name: "About Ajility: The Integrator for Founder-Led Businesses",
        description: "Meet Adam Ayoub and learn how Ajility installs operational clarity, accountability and execution discipline.",
        type: "AboutPage",
      })),
    ],
  }),
  component: AboutPage,
});

const values = [
  { icon: Flag, title: "Ownership", desc: "Accountability for outcomes, not activity." },
  { icon: Target, title: "Execution Discipline", desc: "Clear priorities. Structured rhythm. Consistent delivery." },
  { icon: TrendingUp, title: "Data-Driven Decisions", desc: "Commercial decisions anchored in financial impact and clear performance metrics." },
  { icon: Eye, title: "Clarity Over Comfort", desc: "Clear roles, clear plans, issues confronted early and directly." },
  { icon: RefreshCw, title: "Continuous Improvement", desc: "Build systemised processes that strengthen over time and reduce risk." },
  { icon: ShieldCheck, title: "Trust & Transparency", desc: "Operate with integrity, open communication and transparent reporting." },
];

const situations = [
  { icon: Activity, title: "Founder Overloaded", desc: "Too many decisions rely on you. Little time for strategy." },
  { icon: AlertCircle, title: "Execution Gaps", desc: "Plans defined, but not delivered consistently." },
  { icon: BarChart, title: "Unclear Metrics", desc: "Limited visibility into true performance drivers." },
  { icon: Users, title: "Inconsistent Accountability", desc: "Roles unclear. Priorities shift. Results inconsistent." },
  { icon: Maximize, title: "Scaling Without Structure", desc: "Growth increasing. Structure not keeping pace." },
  { icon: GitMerge, title: "Transition or Exit Prep", desc: "Growth, investment or succession requires stronger structure." },
];

const processFlow = [
  { icon: Search, stage: "Diagnose", description: "Assess current structure, execution bottlenecks and performance gaps." },
  { icon: Minimize2, stage: "Align", description: "Define clear priorities, roles and measurable accountability." },
  { icon: Settings, stage: "Execute", description: "Install structured cadence, KPI visibility and disciplined follow-through." },
  { icon: RefreshCw, stage: "Embed", description: "Systemise processes, strengthen reporting and reduce founder reliance." },
];

function AboutPage() {
  return (
    <>
      {/* Purpose */}
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-28 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="mx-auto max-w-5xl px-5 text-center sm:px-8">
          <span className="eyebrow on-dark">Our purpose</span>
          <h1 className="mt-6 font-serif text-4xl leading-[1.15] sm:text-5xl lg:text-6xl text-balance">
            About Ajility
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-frost/80 sm:text-xl">
            We exist to bring <em className="text-steel-on-dark">clarity</em>,{" "}
            <em className="text-steel-on-dark">accountability</em> and disciplined execution to
            founders and leadership teams of growing businesses, so growth becomes
            structured, scalable and profitable.
          </p>
        </div>
      </section>

      {/* Core values */}
      <PhotoBand
        src={leadershipImg}
        alt="A small leadership team working through notes around a plain table in a modest office"
        surface="mist"
        priority
      />

      <section className="bg-mist py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <span className="eyebrow">How we work</span>
            <h2 className="mt-5 font-serif text-4xl leading-tight text-foreground sm:text-5xl">
              Core values
            </h2>
            <p className="mt-5 text-lg text-charcoal">The principles that guide how we operate.</p>
          </div>
          <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
            {values.map((v, i) => (
              <m.div
                key={v.title}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-card p-8"
              >
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-midnight text-frost">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-serif text-2xl text-foreground">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{v.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who we help */}
      <section className="bg-background py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">Who we help</span>
          <h2 className="mt-5 font-serif text-4xl leading-tight text-foreground sm:text-5xl text-balance">
            Founder-led businesses that have outgrown informal management.
          </h2>

          <div className="mt-16 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl lg:aspect-auto lg:h-full">
                <img
                  src={teamImg}
                  alt="Diverse team collaborating around a table with laptops"
                  width={1400}
                  height={1050}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="space-y-6 lg:col-span-7">
              <div className="card-panel">
                <div className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-steel" />
                  <h3 className="font-serif text-2xl text-foreground">Client profile</h3>
                </div>
                <p className="mt-4 text-lg leading-relaxed text-charcoal">
                  You are a <strong className="text-foreground">Founder, Owner or Managing Director</strong>{" "}
                  still heavily involved in the day-to-day. The business is growing, but
                  you are carrying too much of the load. You want clearer structure, defined
                  priorities and stronger accountability, so you can focus on leading, not
                  firefighting.
                </p>
              </div>
              <div className="card-panel">
                <div className="flex items-center gap-3">
                  <Briefcase className="h-6 w-6 text-steel" />
                  <h3 className="font-serif text-2xl text-foreground">Business profile</h3>
                </div>
                <p className="mt-4 text-lg leading-relaxed text-charcoal">
                  Typically <strong className="text-foreground">15 to 150 employees</strong>.
                  Revenue is growing, but systems and structure have not kept pace. You have
                  capable people, yet execution is inconsistent, ownership is blurred and
                  performance visibility is limited.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common situations */}
      <section className="bg-mist py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2">
              <Zap className="h-5 w-5 text-steel" />
              <span className="eyebrow">Common situations</span>
            </div>
            <h2 className="mt-5 font-serif text-4xl text-foreground sm:text-5xl">
              The operational friction we solve
            </h2>
          </div>
          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {situations.map((s, i) => (
              <m.div
                key={s.title}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <s.icon className="h-6 w-6 text-steel" />
                <h3 className="mt-4 font-serif text-xl text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-charcoal">{s.desc}</p>
              </m.div>
            ))}
          </div>
          <p className="mx-auto mt-14 max-w-2xl text-center font-serif text-2xl italic text-foreground">
            "If these resonate, the business has outgrown informal SME structures."
          </p>
        </div>
      </section>

      {/* The Ajility Way */}
      <section className="bg-background py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <span className="eyebrow">The Ajility Way</span>
            <h2 className="mt-5 font-serif text-4xl leading-tight text-foreground sm:text-5xl text-balance">
              A structured approach to installing clarity, accountability and execution discipline.
            </h2>
          </div>
          <div className="relative mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div className="absolute left-[12%] right-[12%] top-12 hidden h-px bg-gradient-to-r from-transparent via-steel/40 to-transparent lg:block" />
            {processFlow.map((s, i) => (
              <m.div
                key={s.stage}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="relative z-10"
              >
                <div className="mx-auto grid h-24 w-24 place-items-center rounded-full border border-border bg-card shadow-card">
                  <s.icon className="h-8 w-8 text-midnight" />
                </div>
                <div className="mt-6 card-panel text-center">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-steel">
                    Step {i + 1}
                  </span>
                  <h3 className="mt-2 font-serif text-2xl text-foreground">{s.stage}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal">
                    {s.description}
                  </p>
                </div>
              </m.div>
            ))}
          </div>
          <p className="mt-12 text-center font-serif text-2xl italic text-foreground">
            "Clarity translated into structured, sustainable execution."
          </p>
        </div>
      </section>

      {/* Integrator role */}
      <section className="bg-mist py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="eyebrow">The missing link</span>
            <h2 className="mt-5 font-serif text-4xl leading-tight text-foreground sm:text-5xl text-balance">
              The Integrator role
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-charcoal">
              Founder-led businesses often stall at scale because vision outpaces structure.
              The Integrator is built to translate strategy into coordinated execution,
              cross-functional alignment and measurable results.
            </p>
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-border bg-card p-10">
              <div className="flex items-center gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-mist text-steel">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-foreground">The Visionary</h3>
                  <p className="text-sm text-charcoal">The spark and growth driver.</p>
                </div>
              </div>
              <ul className="mt-8 space-y-3 text-charcoal">
                {["Focuses on long-term direction and opportunity",
                  "Drives new ideas and market positioning",
                  "Sets ambition and strategic direction",
                  "Concentrates on \"What\" and \"Why\"",
                  "Sees the big picture"].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-steel" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-midnight p-10 text-frost shadow-elevated">
              <div className="flex items-center gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-white/10 text-steel-on-dark">
                  <Layers className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl">The Integrator</h3>
                  <p className="text-sm text-frost/70">The execution and alignment lead.</p>
                </div>
              </div>
              <ul className="mt-8 space-y-3 text-frost/85">
                {["Translates strategy into structured plans",
                  "Aligns teams around clear priorities",
                  "Drives accountability and follow-through",
                  "Focuses on \"How\" and \"When\"",
                  "Drives measurable execution"].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-steel" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Adam bio */}
      <section className="bg-background py-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid items-center gap-14 md:grid-cols-2">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-3xl border border-border shadow-card">
                <img
                  src="/images/adam-ayoub.jpg"
                  alt="Adam Ayoub, Fractional COO of Ajility"
                  width={800}
                  height={1000}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover object-center"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden max-w-xs rounded-2xl bg-midnight p-6 text-frost shadow-elevated md:block">
                <p className="font-serif text-lg italic">
                  "Execution is the only strategy that the market pays for."
                </p>
              </div>
            </div>
            <div>
              <span className="eyebrow">The Integrator</span>
              <h2 className="mt-4 font-serif text-5xl text-foreground">Adam Ayoub</h2>
              <div className="mt-6 space-y-4 text-lg leading-relaxed text-charcoal">
                <p>
                  Senior Fractional COO and operational leader with 15+ years' experience
                  scaling founder-led businesses and complex commercial operations.
                </p>
                <p>
                  Adam has held General Manager and COO roles across mining services,
                  professional services, construction, logistics, retail and high-growth
                  SMEs. His experience spans P&L responsibility, operational transformation
                  and leadership alignment during periods of growth and transition.
                </p>
                <p>
                  He specialises in installing structure where growth has created
                  complexity, aligning leadership teams, defining clear operating rhythm and
                  driving disciplined, measurable execution so businesses scale predictably.
                </p>
                <p>
                  Operating experience across twenty-plus business systems, from enterprise ERP
                  to trade job-management, ATS/CRM, finance and workforce platforms, selected,
                  implemented and run hands-on, not observed from a steering committee.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="https://www.linkedin.com/in/adamayoub/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium text-foreground transition hover:border-steel hover:text-steel"
                >
                  <Linkedin className="h-4 w-4" /> Connect on LinkedIn
                </a>
                <Link
                  to="/about/adam-ayoub"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium text-foreground transition hover:border-steel hover:text-steel"
                >
                  Read Adam's full background
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-midnight px-5 py-3 text-sm font-semibold text-frost transition hover:bg-midnight-deep"
                >
                  Talk about Integrator support
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* From the operating career */}
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="mt-20 grid gap-8 md:grid-cols-2">
            {["transport-commercial-manager", "fmcg-cfo"].map((id) => {
              const t = getTestimonial(id);
              return (
                <div key={t.id} className="card-panel md:p-10">
                  <blockquote className="font-serif text-xl leading-relaxed text-foreground md:text-2xl">
                    "{t.quote}"
                  </blockquote>
                  <p className="mt-6 text-sm text-charcoal">{t.role}</p>
                  <p className="mt-1 text-sm text-charcoal">
                    {relationshipLabel[t.relationship]}
                    {t.context ? `. ${t.context}` : ""}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PageFaqs faqs={faqs} />

      <NextStep
        heading="See where your operation stands"
        body="Answer 21 plain statements about how the business runs, from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Book a fit call"
        primaryTo={fitCallUrl("about_footer")}
        primaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
        secondaryLabel="Take the scan first"
        secondaryTo={scanUrl("about_footer")}
      />
    </>
  );
}
